import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import useSettings from "./hooks/useSettings";
import "./styles/animations.css";
import IOSInstallPrompt from "./components/IOSInstallPrompt";
import { estratizaColors, estratizaGradients } from "./styles/estratizaTheme";

const defaultLogoLight = "/estratiza-logo.svg";
const defaultLogoDark = "/estratiza-logo.svg";
const defaultLogoFavicon = "/estratiza-mark.svg";
const defaultPrimaryColor = estratizaColors.gold;
const defaultAppName = process.env.REACT_APP_NAME_SYSTEM || "Estratiza Hub";

const normalizeThemeColor = (value, fallback) => {
  if (typeof value !== "string") return fallback;
  const normalized = value.trim();
  return /^#[0-9a-f]{6}$/i.test(normalized) ? normalized : fallback;
};

const getStoredThemeColor = () => {
  const storedLight = localStorage.getItem("primaryColorLight");
  const storedDark = localStorage.getItem("primaryColorDark");
  const storedColor = storedLight || storedDark;
  const normalized = normalizeThemeColor(storedColor, defaultPrimaryColor);

  // Material-UI encerra a aplicacao se receber uma cor antiga/invalida.
  // Limpe apenas esse cache visual; sessao, usuario e demais dados permanecem.
  if (storedColor && normalized !== storedColor.trim()) {
    localStorage.removeItem("primaryColorLight");
    localStorage.removeItem("primaryColorDark");
  }
  return normalized;
};

// No cold start de uma notificacao nao ha postMessage. Preserve o destino
// sincronicamente, antes de as rotas e a autenticacao poderem redirecionar.
try {
  const initialPathname = window.location.pathname;
  if (/^\/tickets\/[A-Za-z0-9-]+$/.test(initialPathname)) {
    localStorage.setItem("estratizaPushTarget", initialPathname);
  }
} catch (_err) {
  // Falha de storage nunca pode impedir a abertura normal do aplicativo.
}

const queryClient = new QueryClient();

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { this.setState({ stack: (error?.stack || "") }); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: 16, padding: 32 }}>
          <h2 style={{ color: "#c62828" }}>Ocorreu um erro inesperado</h2>
          <p style={{ color: "#555", textAlign: "center" }}>{this.state.error?.message || "Tente recarregar a pagina"}</p>
          <pre style={{ fontSize: 10, color: "#888", background: "#f5f5f5", padding: 12, borderRadius: 6, maxWidth: "100%", overflow: "auto", maxHeight: 200, textAlign: "left" }}>{this.state.stack || ""}</pre>
          <button onClick={() => window.location.reload()} style={{ padding: "10px 24px", background: estratizaColors.gold, color: estratizaColors.black, border: "none", borderRadius: 8, cursor: "pointer", fontSize: 15, fontWeight: 700 }}>Recarregar</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage = getStoredThemeColor();
  const appNameLocalStorage = defaultAppName;
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(
    preferredTheme || "light"
  );
  const [primaryColorLight, setPrimaryColorLight] =
    useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] =
    useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(null);
  const [appLogoDark, setAppLogoDark] = useState(null);
  const [appLogoFavicon, setAppLogoFavicon] = useState(null);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const [brandingReady, setBrandingReady] = useState(false);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          // Scrollbar styles melhorados mas usando cores do tema
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor:
                mode === "light" ? primaryColorLight : primaryColorDark, // Usa cores do tema
              borderRadius: "999px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: mode === "light" ? estratizaColors.ivorySoft : estratizaColors.blackSoft,
              borderRadius: "4px",
            },
          },

          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#E0E0E0" : "#404040",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: mode === "light" ? "#BDBDBD" : "#505050",
              }
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          },

          palette: {
            type: mode,
            primary: {
              main: mode === "light" ? primaryColorLight : primaryColorDark, // Usa cores dinâmicas
              light: mode === "light"
                ? `${primaryColorLight}80`
                : `${primaryColorDark}80`,
              dark: mode === "light"
                ? `${primaryColorLight}CC`
                : `${primaryColorDark}CC`,
              contrastText: estratizaColors.black,
            },
            background: {
              default: mode === "light" ? estratizaColors.ivory : estratizaColors.black,
              paper: mode === "light" ? "#FFFCF5" : estratizaColors.graphite,
            },
            text: {
              primary: mode === "light" ? estratizaColors.ink : "#F7F0DF",
              secondary: mode === "light" ? estratizaColors.mutedLight : estratizaColors.mutedDark,
            },
            divider: mode === "light" ? "rgba(155,113,24,0.22)" : "rgba(212,175,55,0.18)",
            action: {
              hover: mode === "light" ? "rgba(212,175,55,0.10)" : "rgba(212,175,55,0.12)",
              selected: mode === "light" ? "rgba(212,175,55,0.16)" : "rgba(212,175,55,0.18)",
            },
            textPrimary:
              mode === "light" ? primaryColorLight : primaryColorDark,
            borderPrimary:
              mode === "light" ? primaryColorLight : primaryColorDark,
            dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
            light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
            fontColor: mode === "light" ? primaryColorLight : primaryColorDark,
            tabHeaderBackground: mode === "light" ? "#EFE7D4" : estratizaColors.graphiteElevated,
            optionsBackground: mode === "light" ? "#FFFCF5" : estratizaColors.graphite,
            fancyBackground: mode === "light" ? estratizaGradients.lightCanvas : estratizaGradients.darkCanvas,
            total: mode === "light" ? "#FFFCF5" : estratizaColors.graphite,
            messageIcons: mode === "light" ? estratizaColors.goldDark : estratizaColors.goldLight,
            inputBackground: mode === "light" ? "#FFFCF5" : estratizaColors.graphiteElevated,
            barraSuperior: estratizaColors.black,
          },

          typography: {
            fontFamily: [
              'Inter',
              'Roboto',
              '-apple-system',
              'BlinkMacSystemFont',
              '"Segoe UI"',
              '"Helvetica Neue"',
              'Arial',
              'sans-serif',
            ].join(','),
            h1: {
              fontWeight: 700,
              letterSpacing: '-0.025em',
            },
            h2: {
              fontWeight: 700,
              letterSpacing: '-0.025em',
            },
            h3: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h4: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h5: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h6: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            button: {
              fontWeight: 600,
              textTransform: 'none',
              letterSpacing: '0.025em',
            },
          },

          shape: {
            borderRadius: 10,
          },
          overrides: {
            // Botões usando cor do tema
            MuiButton: {
              root: {
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.025em',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              },
              contained: {
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                }
              },
              containedPrimary: {
                color: estratizaColors.black,
                background: estratizaGradients.gold,
                boxShadow: '0 8px 22px rgba(155,113,24,0.22)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FFE49A 0%, #D4AF37 55%, #A77918 100%)',
                  boxShadow: '0 10px 26px rgba(155,113,24,0.32)',
                },
              }
            },

            MuiContainer: {
              root: {
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
                maxWidth: 'none !important',
                width: '100% !important',
              },
              maxWidthLg: {
                maxWidth: 'none !important',
              },
              maxWidthMd: {
                maxWidth: 'none !important',
              },
              maxWidthSm: {
                maxWidth: 'none !important',
              },
              maxWidthXl: {
                maxWidth: 'none !important',
              },
              maxWidthXs: {
                maxWidth: 'none !important',
              },
            },
          
            MuiPaper: {
              root: {
                backgroundImage: 'none',
              },
              rounded: {
                borderRadius: 16,
              },
              elevation1: {
                boxShadow: mode === 'light'
                  ? '0 12px 34px rgba(67,48,10,0.09)'
                  : '0 14px 36px rgba(0,0,0,0.38)',
              },
              elevation2: {
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
              },
              elevation3: {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }
            },

            // ⭐ ADICIONAR: Proteção específica para menus
            MuiMenu: {
              paper: {
                width: 'auto !important',
                maxWidth: '300px !important',
                minWidth: '180px !important',
              }
            },

            // ⭐ ADICIONAR: Proteção específica para popovers
            MuiPopover: {
              paper: {
                width: 'auto !important',
                maxWidth: '300px !important',
                minWidth: 'auto !important',
              }
            },

            // Inputs melhorados
            MuiTextField: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === "light" ? "#ccc" : "#555",
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === "light" ? primaryColorLight : primaryColorDark,
                      borderWidth: 2,
                    }
                  }
                }
              }
            },

            // Tabs usando cor do tema
            MuiTab: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.025em',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: mode === "light"
                    ? `${primaryColorLight}08`
                    : `${primaryColorDark}08`,
                },
                '&.Mui-selected': {
                  color: mode === "light" ? primaryColorLight : primaryColorDark,
                }
              }
            },

            // Drawer sem bordas
            MuiDrawer: {
              paper: {
                border: 'none',
                backgroundColor: `${estratizaColors.black} !important`,
                backgroundImage: `${estratizaGradients.sidebar} !important`,
                color: `${estratizaColors.goldLight} !important`,
              }
            },

            MuiCard: {
              root: {
                borderRadius: 16,
                border: mode === 'light'
                  ? '1px solid rgba(155,113,24,0.14)'
                  : '1px solid rgba(212,175,55,0.14)',
                boxShadow: mode === 'light'
                  ? '0 12px 34px rgba(67,48,10,0.08)'
                  : '0 14px 38px rgba(0,0,0,0.34)',
              },
            },

            MuiDialog: {
              paper: {
                borderRadius: 18,
                border: mode === 'light'
                  ? '1px solid rgba(155,113,24,0.16)'
                  : '1px solid rgba(212,175,55,0.18)',
              },
            },

            MuiTableHead: {
              root: {
                backgroundColor: mode === 'light' ? '#EFE7D4' : estratizaColors.graphiteElevated,
                '& .MuiTableCell-head': {
                  color: mode === 'light' ? estratizaColors.ink : estratizaColors.goldLight,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                },
              },
            },

            MuiTableCell: {
              root: {
                borderBottom: mode === 'light'
                  ? '1px solid rgba(155,113,24,0.12)'
                  : '1px solid rgba(212,175,55,0.10)',
              },
            },

            MuiTooltip: {
              tooltip: {
                color: estratizaColors.goldLight,
                backgroundColor: '#090806',
                border: '1px solid rgba(212,175,55,0.22)',
                borderRadius: 8,
                fontWeight: 600,
              },
            },

            MuiChip: {
              root: {
                borderRadius: 8,
                fontWeight: 600,
              },
              colorPrimary: {
                color: estratizaColors.black,
                backgroundColor: estratizaColors.gold,
              },
            },

            // AppBar transparente
            MuiAppBar: {
              root: {
                boxShadow: 'none',
              }
            }
          },

          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (
              appLogoDark === defaultLogoDark &&
              appLogoLight !== defaultLogoLight
            ) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (
              appLogoDark !== defaultLogoDark &&
              appLogoLight === defaultLogoLight
            ) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight, // Essas são as cores que vêm do tema dinâmico
    ]
  );

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    let active = true;
    const readSetting = (key, fallback) =>
      getPublicSetting(key).catch((error) => {
        console.log("Error reading setting", error);
        return fallback;
      });

    Promise.all([
      readSetting("primaryColorLight", estratizaColors.gold),
      readSetting("primaryColorDark", estratizaColors.goldLight),
      readSetting("appLogoLight", null),
      readSetting("appLogoDark", null),
      readSetting("appLogoFavicon", null),
      readSetting("appName", defaultAppName),
    ]).then(([lightColor, darkColor, lightFile, darkFile, faviconFile, name]) => {
      if (!active) return;

      const backendUrl = getBackendUrl();
      const resolvedLight = lightFile ? `${backendUrl}/public/${lightFile}` : defaultLogoLight;
      const resolvedDark = darkFile ? `${backendUrl}/public/${darkFile}` : (lightFile ? resolvedLight : defaultLogoDark);
      const resolvedFavicon = faviconFile ? `${backendUrl}/public/${faviconFile}` : defaultLogoFavicon;
      const resolvedName = name || defaultAppName;

      setPrimaryColorLight(normalizeThemeColor(lightColor, estratizaColors.gold));
      setPrimaryColorDark(normalizeThemeColor(darkColor, estratizaColors.goldLight));
      setAppLogoLight(resolvedLight);
      setAppLogoDark(resolvedDark);
      setAppLogoFavicon(resolvedFavicon);
      setAppName(resolvedName);
      window.localStorage.setItem("appName", resolvedName);

      const splashLogo = document.getElementById("splash-logo");
      if (splashLogo) {
        splashLogo.onload = () => { splashLogo.style.visibility = "visible"; };
        splashLogo.onerror = () => { splashLogo.style.visibility = "hidden"; };
        splashLogo.src = resolvedDark || resolvedLight;
      }

      setBrandingReady(true);
    });

    return () => { active = false; };
    // A leitura unica e deliberada: o Whitelabel atualiza o contexto apos upload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? primaryColorLight : primaryColorDark
    );
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <ErrorBoundary>
    <>
      {brandingReady && (
        <>
          <Favicon url={appLogoFavicon || defaultLogoFavicon} />
          <ColorModeContext.Provider value={{ colorMode }}>
            <ThemeProvider theme={theme}>
              <QueryClientProvider client={queryClient}>
                <ActiveMenuProvider>
                  <Routes />
                </ActiveMenuProvider>
              </QueryClientProvider>
            </ThemeProvider>
          </ColorModeContext.Provider>
          <IOSInstallPrompt />
        </>
      )}
    </>
    </ErrorBoundary>
  );
};

export default App;
